import React, { useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { useForm, FormProvider } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  useCreateMaterialTypeMutation,
  useGetMaterialTypeQuery,
  useGetMaterialTypesQuery,
  usePatchMaterialTypeMutation,
  useArchiveMaterialTypeMutation,
} from 'services/materialTypes'
import { useCreatePriceEntryMutation } from 'services/priceEntries'
import { setSnackBar } from 'state/ui'
import { formOptions } from 'utils/validation/material'
import { trackEvent } from 'utils/tracking'
import {
  PRICE_TYPES,
  DEFAULT_PRECISION,
  CHURN_ZERO_EVENTS as cz,
  WASTE_CODE,
  MATERIAL_TYPE_RULES,
} from 'utils/constants'
import AlertSnackbar from 'components/generic/snackbars'
import Loading from 'components/generic/Loading'
import WizardStepper from 'components/generic/stepper/WizardStepper'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

import GeneralPropertiesForm from 'components/containers/commodities/new-material/components/GeneralPropertiesForm'
import PricingForm from 'components/containers/commodities/new-material/components/PricingForm'
import MaterialConfirmationForm from 'components/containers/commodities/new-material/components/MaterialConfirmationForm'
import MaterialComplianceForm from 'components/containers/commodities/new-material/components/MaterialComplianceForm'

const NewMaterialContainer = ({ onFormStateChange, onHideModal }) => {
  const { materialTypeId } = useSelector(state => state.ui.formModalProps)
  const [showFinishedForm, setShowFinishedForm] = useState(false)
  const [isArchiveModalUp, setIsArchiveModalUp] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isFinished, setIsFinished] = useState(false)
  const [materialName, setMaterialName] = useState('')
  const [materialCode, setMaterialCode] = useState('')
  const [materialNameDuplicated, setMaterialNameDuplicated] = useState(false)
  const [materialCodeDuplicated, setMaterialCodeDuplicated] = useState(false)
  const { defaultCurrency } = useSelector(state => state.settings)
  const { canArchiveFlag, enableMaterialPaymentHolds } = useSelector(state => state.featureFlags?.variables || {})

  const [title, setTitle] = useState('Add New Material')

  const isUpdate = !!materialTypeId
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const defaultValues = {
    general: {
      materialName: '',
      commodity: null,
      materialCode: '',
      isriCode: '',
      accountingCode: '',
      accountingExpenseCode: '',
      notes: '',
      tareWeightRequired: false,
    },
    paymentHold: {
      enabled: false,
      days: 0,
    },
    paymentMethods: [],
    complianceOptOut: false,
  }
  const methods = useForm({
    ...formOptions,
    defaultValues,
    mode: 'onTouched',
  })

  const { handleSubmit, control, formState, reset, watch, setValue, getValues, trigger } = methods
  const { isDirty, isValid } = formState

  const { data: materialTypeData, isLoading: isLoadingMaterialTypeData } = useGetMaterialTypeQuery(
    { materialTypeId },
    { refetchOnMountOrArgChange: true, skip: !isUpdate },
  )

  const { data: materialTypesNameMatch } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ search: materialName }), pageSize: 50 },
    { skip: !materialName },
  )

  const { data: materialTypesCodeMatch } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ search: materialCode }), pageSize: 50 },
    { skip: !materialCode },
  )

  const [createPriceEntry, { isLoading: isCreatingPriceEntry }] = useCreatePriceEntryMutation()
  const [createMaterialType, { isLoading: isCreatingMaterialType }] = useCreateMaterialTypeMutation()
  const [patchMaterialType, { isLoading: isPatchingMaterialType }] = usePatchMaterialTypeMutation()
  const [archiveMaterialType, { isLoading: isArchivingMaterialType }] = useArchiveMaterialTypeMutation()

  const isLoading =
    isCreatingMaterialType ||
    isCreatingPriceEntry ||
    isLoadingMaterialTypeData ||
    isPatchingMaterialType ||
    isArchivingMaterialType

  const formatMaterial = material => {
    const paymentHoldRule = material.rules?.find(rule => rule.type === MATERIAL_TYPE_RULES.PAYMENT_HOLD)
    const paymentMethodRule = material.rules?.find(rule => rule.type === MATERIAL_TYPE_RULES.PAYMENT_METHOD)
    return {
      general: {
        ...material,
        materialCode: material?.code || '',
        materialName: material?.commonName || '',
        commodityName: material?.commodity?.name || '',
        isriCode: material?.isriCode || '',
        accountingCode: material?.accountingCode || '',
        accountingExpenseCode: material?.accountingExpenseCode || '',
        notes: material?.notes || '',
        tareWeightRequired: material?.tareWeightRequired,
      },
      paymentHold: {
        enabled: !!paymentHoldRule,
        days: paymentHoldRule?.parameters?.days,
      },
      paymentMethods: paymentMethodRule?.parameters?.paymentMethods,
      complianceOptOut: material?.complianceOptOut || false,
    }
  }

  useEffect(() => {
    const duplicate = materialTypesNameMatch?.Items?.find(
      materialType => materialType?.commonName?.toLowerCase() === materialName?.toLowerCase(),
    )
    setMaterialNameDuplicated(!!duplicate && duplicate?.id !== materialTypeData?.id)
  }, [materialTypeData, materialTypesNameMatch, materialName])

  useEffect(() => {
    const duplicate = materialTypesCodeMatch?.Items?.find(
      materialType => materialType?.code?.toLowerCase() === materialCode?.toLowerCase(),
    )
    setMaterialCodeDuplicated(!!duplicate && duplicate?.id !== materialTypeData?.id)
  }, [materialTypeData, materialTypesCodeMatch, materialCode])

  useEffect(() => {
    if (materialTypeData) {
      setTitle(materialTypeData?.commonName)
      const formattedMaterial = formatMaterial(materialTypeData)
      reset(formattedMaterial)
    }
  }, [materialTypeData])

  const handleError = error => {
    const errorMsg = error?.data?.error || `There was an error ${isUpdate ? 'updating' : 'creating'} the material`
    trackEvent({
      eventName: isUpdate ? cz.UPDATE_MATERIAL : cz.ADD_MATERIAL,
      errorMsg,
    })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }
  const handleSuccess = () => {
    trackEvent(isUpdate ? cz.UPDATE_MATERIAL : cz.ADD_MATERIAL)
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `Material successfully ${isUpdate ? 'updated' : 'created'}`,
      }),
    )
  }

  const handleValidatePartial = async step => {
    if (isUpdate) {
      const {
        paymentHold: { enabled, days },
        // paymentMethods, // commented out until v2 of payment holds
      } = getValues()
      // payment method validation commented out until v2 of payment holds
      // const valid = !enabled || (paymentMethods?.length > 0 && days > 0)
      const valid = !enabled || days > 0
      return !isDirty || !valid
    }
    let fields = []
    switch (step) {
      case 0:
        fields = ['general']
        break
      case 1:
        fields = isUpdate ? ['paymentHold', 'paymentMethods'] : ['pricing']
        break
      case 2:
        fields = ['paymentHold', 'paymentMethods']
        break
      default:
        break
    }
    const validationResult = await trigger(fields)
    return validationResult && !materialNameDuplicated && !materialCodeDuplicated
  }

  const steps = [
    {
      label: `${isUpdate ? 'Material' : 'General'} Properties`,
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: [isUpdate ? 'SAVE' : 'NEXT: PRICING', 'CANCEL'],
      contentComponent: (
        <GeneralPropertiesForm
          setMaterialName={setMaterialName}
          setMaterialCode={setMaterialCode}
          materialNameDuplicated={materialNameDuplicated}
          materialCodeDuplicated={materialCodeDuplicated}
          isWasteMaterial={materialTypeData?.code === WASTE_CODE}
        />
      ),
    },
    ...(!isUpdate
      ? [
          {
            label: 'Pricing',
            showStepper: true,
            stepOptional: false,
            footerButtonTitles: [enableMaterialPaymentHolds ? 'NEXT: COMPLIANCE' : 'FINISH', 'BACK'],
            contentComponent: <PricingForm control={control} watch={watch} setValue={setValue} />,
          },
        ]
      : []),
    ...(enableMaterialPaymentHolds
      ? [
          {
            label: 'Compliance',
            showStepper: true,
            stepOptional: false,
            footerButtonTitles: ['FINISH', 'BACK'],
            contentComponent: <MaterialComplianceForm methods={methods} isUpdate={isUpdate} />,
          },
        ]
      : []),
  ]

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const formatMaterialTypeData = data => ({
    date: Date.now(),
    code: data.general.materialCode,
    commodityName: data.general.commodity?.name,
    commodityId: data.general.commodity?.id,
    commonName: data.general.materialName,
    commodity: data.general.commodity,
    isriCode: data.general.isriCode || '',
    accountingCode: data.general.accountingCode || '',
    accountingExpenseCode: data.general.accountingExpenseCode || '',
    notes: data.general.notes || '',
    tareWeightRequired: data.general.tareWeightRequired,
    complianceOptOut: data.complianceOptOut,
    rules: [
      {
        enabled: data.paymentHold.enabled,
        paymentHold: {
          days: data.paymentHold.days,
        },
        paymentMethods: data.paymentMethods,
      },
    ],
  })

  const formatPriceEntryData = (data, resMaterialTypeId) => {
    const buyBenchmarkId = data.pricing.buyBenchmark?.id
    const sellBenchmarkId = data.pricing.sellBenchmark?.id
    return {
      materialTypeId: resMaterialTypeId,
      materialCode: data.general.materialCode,
      buyPrice: {
        isBuyPrice: true,
        price: {
          amount: Math.floor((Number(data.pricing.buyPrice) || 0) * 10 ** DEFAULT_PRECISION),
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        priceType: data.pricing.buyFormula,
        minMaxRange: data.pricing.buyMinMaxRange,
        differential: {
          amount: Math.floor((Number(data.pricing.buyDifferential) || 0) * 10 ** DEFAULT_PRECISION),
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        recoveryPercentage: Math.floor(Number(data.pricing.buyRecovery) || 100),
        otherCosts: {
          amount: Math.floor((Number(data.pricing.buyOtherCosts) || 0) * 10 ** DEFAULT_PRECISION),
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        benchmarkId: buyBenchmarkId,
        date: Date.now(),
      },
      sellPrice: {
        isBuyPrice: false,
        price: {
          amount: Math.floor((Number(data.pricing.sellPrice) || 0) * 10 ** DEFAULT_PRECISION),
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        priceType: data.pricing.sellFormula,
        minMaxRange: data.pricing.sellMinMaxRange,
        differential: {
          amount: Math.floor((Number(data.pricing.sellDifferential) || 0) * 10 ** DEFAULT_PRECISION),
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        recoveryPercentage: Math.floor(Number(data.pricing.sellRecovery) || 100),
        otherCosts: {
          amount: Math.floor((Number(data.pricing.sellOtherCosts) || 0) * 10 ** DEFAULT_PRECISION),
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        benchmarkId: sellBenchmarkId,
        date: Date.now(),
      },
      weightUnits: data.pricing.buyUnit,
    }
  }

  const handleSubmitPartial = async () => {
    if (isValid) {
      if (materialNameDuplicated || materialCodeDuplicated) return
      const materialUpdated = getValues()
      const formattedMaterialType = formatMaterialTypeData(materialUpdated)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { date, commodityName, user, defaultPriceEntry, ...materialType } = {
        ...materialTypeData,
        ...formattedMaterialType,
      }

      await patchMaterialType({ body: materialType, materialTypeId })
        .unwrap()
        .then(() => {
          handleSuccess()
          const {
            commodity: { urlName },
            commonName,
            id,
          } = materialType
          const matURI = `${encodeURIComponent(urlName)}/${encodeURIComponent(commonName)}/${id}/overview`
          navigate(matURI)
        })
        .catch(handleError)
    }
  }

  const onSubmit = async data => {
    const materialType = formatMaterialTypeData(data)

    if (isFinished) {
      onHideModal()
    } else {
      const buyPriceType = data.pricing.buyFormula
      const sellPriceType = data.pricing.sellFormula
      const buyBenchmark = data.pricing.buyBenchmark || null
      const sellBenchmark = data.pricing.sellBenchmark || null

      if (
        (buyPriceType === PRICE_TYPES.FORMULA.id && !buyBenchmark) ||
        (sellPriceType === PRICE_TYPES.FORMULA.id && !sellBenchmark)
      ) {
        setErrorMessage('Benchmark Price is required.')
      } else {
        await createMaterialType({ materialType })
          .unwrap()
          .then(async resMaterialType => {
            const priceEntry = formatPriceEntryData(data, resMaterialType.id)
            await createPriceEntry({ priceEntry })
              .unwrap()
              .then(payload => {
                handleSuccess(payload)
                setIsFinished(true)
                setShowFinishedForm(true)
              })
          })
          .catch(error => handleError(error))
      }
    }
  }

  const onAddAnotherMaterial = useCallback(() => {
    reset({
      general: {
        materialName: '',
        commodity: null,
        materialCode: '',
        isriCode: '',
        accountingCode: '',
        accountingExpenseCode: '',
        notes: '',
        tareWeightRequired: false,
      },
    })
    setIsFinished(false)
    setShowFinishedForm(false)
    setMaterialName('')
    setMaterialCode('')
  }, [])

  const handleArchiveClick = () => setIsArchiveModalUp(true)
  const handleArchiveMaterial = async () => {
    await archiveMaterialType({ materialTypeId })
      .unwrap()
      .then(() => {
        trackEvent(cz.ARCHIVE_MATERIAL)
        navigate('/app/commodities/commodity-list')
        onHideModal()
      })
      .catch(error => {
        const errorMsg = error?.data?.error || 'An error occurred archiving the material'
        dispatch(
          setSnackBar({
            snackbarType: 'error',
            snackbarMessage: errorMsg,
          }),
        )
        trackEvent({
          eventName: cz.ARCHIVE_MATERIAL,
          errorMsg,
        })
      })
  }
  return (
    <FormProvider {...methods}>
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      <AlertModal
        open={isArchiveModalUp}
        modalType="void"
        title="Archive Material"
        question="Are you sure you want to archive this material?"
        description="Archiving a material is irreversible at this time."
        confirmLabel="YES, ARCHIVE THIS MATERIAL"
        cancelLabel="GO BACK"
        onClose={() => setIsArchiveModalUp(false)}
        onSubmit={handleArchiveMaterial}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && <Loading />}
        {showFinishedForm ? (
          <MaterialConfirmationForm onAddAnotherMaterial={onAddAnotherMaterial} onHideModal={onHideModal} />
        ) : (
          <WizardStepper
            steps={steps}
            onHideModal={onHideModal}
            title={title}
            validationCheck
            nonLinear={isUpdate}
            disableAlertModal={!isDirty}
            onSubmitPartial={handleSubmitPartial}
            onValidatePartial={handleValidatePartial}
            canArchive={canArchiveFlag}
            handleArchiveClick={handleArchiveClick}
          />
        )}
      </form>
    </FormProvider>
  )
}

NewMaterialContainer.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

NewMaterialContainer.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default NewMaterialContainer
